import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Car, ArrowRight, Lock } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function DriverLoginPage() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [requestId, setRequestId] = useState("");
  const [loading, setLoading] = useState(false);

  const submitStep1 = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login-step1`, { email, password });
      setRequestId(res.data.request_id);
      setStep(2);
      toast.success("Check your inbox for the 6-digit code");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login-step2`, { request_id: requestId, otp });
      const userData = res.data || {};
      const sessionToken = userData.session_token;
      if (sessionToken) localStorage.setItem("session_token", sessionToken);
      // Strip session_token before passing as user
      const { session_token: _t, ...userOnly } = userData;
      await loginWithToken(sessionToken, userOnly);
      toast.success("Welcome back!");
      const role = userOnly.role;
      if (role === "driver") navigate("/driver");
      else if (role === "admin" || role === "super_admin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0F0F0F] border border-white/5 rounded-sm p-8" data-testid="driver-login-card">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-[#C5A059]/15 flex items-center justify-center mx-auto mb-4">
            <Car className="w-6 h-6 text-[#C5A059]" />
          </div>
          <h1 className="text-2xl font-bold text-white">Driver Sign In</h1>
          <p className="text-white/50 text-xs mt-1">Access your assigned transfers calendar</p>
        </div>

        {step === 1 ? (
          <form onSubmit={submitStep1} className="space-y-4">
            <div>
              <label className="text-white/60 text-xs uppercase mb-1 block">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input-dark" data-testid="driver-login-email" />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase mb-1 block">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="input-dark" data-testid="driver-login-password" />
            </div>
            <Button type="submit" disabled={loading} className="btn-gold w-full" data-testid="driver-login-submit">
              {loading ? "Sending OTP..." : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
            <p className="text-white/40 text-xs text-center mt-2">
              Forgot password? <Link to="/?forgot=1" className="text-[#C5A059] hover:underline">Reset it</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={submitStep2} className="space-y-4">
            <p className="text-white/60 text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-[#C5A059]" /> 6-digit code emailed to <strong className="text-white">{email}</strong></p>
            <Input value={otp} onChange={(e) => setOtp(e.target.value)} required maxLength={6} placeholder="• • • • • •" className="input-dark text-center text-2xl tracking-[0.5em]" data-testid="driver-login-otp" />
            <Button type="submit" disabled={loading || otp.length < 6} className="btn-gold w-full" data-testid="driver-login-otp-submit">
              {loading ? "Verifying..." : "Verify & Sign In"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
